import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Camera, ScanLine, X, Loader2, Video } from "lucide-react";

export const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const fileUrl = (p) => `${BACKEND}/api/files/${p}`;

export const uploadProd = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/prod/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
};

let MEDIA = null;
export const mediaCfg = async () => {
  if (!MEDIA) { try { MEDIA = (await api.get("/media/settings")).data; } catch { MEDIA = { img_max_kb: 150, img_max_px: 1600, video_max_mb: 20, video_max_sec: 60 }; } }
  return MEDIA;
};
export const resetMediaCfg = () => { MEDIA = null; };

export const compressImage = async (file) => {
  const c = await mediaCfg();
  if (!/^image\/(jpeg|png|webp)$/.test(file.type) || file.size <= c.img_max_kb * 1024) return file;
  try {
    const bmp = await createImageBitmap(file);
    const k = Math.min(1, c.img_max_px / Math.max(bmp.width, bmp.height));
    const cv = document.createElement("canvas");
    let blob = null; let k2 = k;
    for (let t = 0; t < 4; t++) {
      cv.width = Math.round(bmp.width * k2); cv.height = Math.round(bmp.height * k2);
      cv.getContext("2d").drawImage(bmp, 0, 0, cv.width, cv.height);
      for (const q of [0.85, 0.75, 0.65, 0.55, 0.45]) {
        blob = await new Promise((r) => cv.toBlob(r, "image/jpeg", q));
        if (blob && blob.size <= c.img_max_kb * 1024) break;
      }
      if (!blob || blob.size <= c.img_max_kb * 1024 || Math.min(cv.width, cv.height) < 400) break;
      k2 *= 0.8;
    }
    return blob && blob.size < file.size ? new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }) : file;
  } catch { return file; }
};

const videoDuration = (file) => new Promise((res) => {
  const v = document.createElement("video");
  v.preload = "metadata";
  v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); res(v.duration); };
  v.onerror = () => res(0);
  v.src = URL.createObjectURL(file);
});

export const ScalePhoto = ({ testid, label = "Foto timbangan (wajib)", photo, setPhoto, setGross }) => {
  const [st, setSt] = useState("");
  const onChange = async (a) => {
    const p = a[0] || ""; setPhoto(p); if (!p) { setSt(""); return; }
    setSt("AI membaca timbangan…");
    try { const { data } = await api.post("/prod/scale-read", { path: p }); if (data.grams != null) { setGross(String(data.grams)); setSt(`AI membaca ${data.grams} g — cek & koreksi bila perlu`); } else setSt("Angka tidak terbaca — isi berat manual"); }
    catch (e) { setSt(e?.response?.data?.detail || "AI gagal membaca — isi berat manual"); }
  };
  return <div><MediaInput testid={testid} label={label} value={photo ? [photo] : []} onChange={onChange} />{st && <p className="text-[11px] text-sky-700 mt-1" data-testid={`${testid}-ai`}>{st}</p>}</div>;
};

export const MediaInput = ({ value = [], onChange, max = 1, allowVideo = false, testid, label = "Foto bukti", onBusy, uploader = uploadProd, maxSec = 10, videoOnly = false, accept = "" }) => {
  const [busy, setBusyRaw] = useState(false);
  const setBusy = (b) => { setBusyRaw(b); onBusy?.(b); };
  const pick = async (e) => {
    const files = [...e.target.files].slice(0, max - value.length);
    e.target.value = "";
    setBusy(true);
    try {
      const out = [...value];
      const cfg = await mediaCfg();
      const lim = Math.min(maxSec, cfg.video_max_sec);
      for (const f of files) {
        if (f.type.startsWith("video/")) {
          if (!allowVideo) { toast.error("Video tidak diizinkan di sini"); continue; }
          if (f.size > cfg.video_max_mb * 1024 * 1024) { toast.error(`Video ${(f.size / 1048576).toFixed(1)} MB — maksimal ${cfg.video_max_mb} MB`); continue; }
          const d = await videoDuration(f);
          if (d > lim + 0.5) { toast.error(`Video ${Math.round(d)} detik — maksimal ${lim} detik`); continue; }
          out.push((await uploader(f)).path);
          continue;
        } else if (videoOnly) { toast.error("Pilih file video"); continue; }
        out.push((await uploader(f.type.startsWith("image/") ? await compressImage(f) : f)).path);
      }
      onChange(out);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload gagal"); } finally { setBusy(false); }
  };
  return (
    <div data-testid={testid}>
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      <div className="flex flex-wrap gap-2 items-center">
        {value.map((p) => (
          <span key={p} className="relative">
            {/\.(mp4|mov|webm|m4v)$/i.test(p) ? <span className="w-14 h-14 rounded border flex items-center justify-center bg-slate-100"><Video className="w-5 h-5" /></span>
              : /\.pdf$/i.test(p) ? <a href={fileUrl(p)} target="_blank" rel="noreferrer" className="w-14 h-14 rounded border flex items-center justify-center bg-rose-50 text-rose-700 text-[11px] font-bold">PDF</a>
              : <img src={fileUrl(p)} alt="bukti" className="w-14 h-14 object-cover rounded border" />}
            <button type="button" onClick={() => onChange(value.filter((x) => x !== p))} className="absolute -top-1.5 -right-1.5 bg-white rounded-full border"><X className="w-3 h-3" /></button>
          </span>
        ))}
        {value.length < max && (
          <label className="w-14 h-14 rounded border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-sky-400" data-testid={`${testid}-add`}>
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5 text-slate-400" />}
            <input type="file" className="hidden" accept={accept || (videoOnly ? "video/*" : allowVideo ? "image/*,video/*" : "image/*")} capture={accept.includes("pdf") ? undefined : "environment"} multiple={max > 1} onChange={pick} data-testid={`${testid}-input`} />
          </label>
        )}
      </div>
    </div>
  );
};

export const CameraScanner = ({ onCode, onClose }) => {
  const ref = useRef(null);
  useEffect(() => {
    let sc;
    let stopped = false;
    import("html5-qrcode").then(async ({ Html5Qrcode }) => {
      if (stopped) return;
      const devs = await (navigator.mediaDevices?.enumerateDevices?.() || Promise.resolve([])).catch(() => []);
      if (!devs.some((d) => d.kind === "videoinput")) { toast.error("Tidak ada kamera di perangkat ini — ketik kode manual"); onClose(); return; }
      if (stopped) return;
      sc = new Html5Qrcode("prod-scan-box");
      ref.current = sc;
      sc.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 230, height: 230 } }, (txt) => { onCode(txt); }, () => {})
        .catch(() => { toast.error("Kamera tidak bisa dibuka — ketik kode manual"); onClose(); });
    });
    return () => { stopped = true; if (ref.current) { ref.current.stop().catch(() => {}); } };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 flex flex-col items-center justify-center p-4" data-testid="scan-camera">
      <div id="prod-scan-box" className="w-full max-w-sm bg-black rounded-xl overflow-hidden" />
      <button type="button" onClick={onClose} className="mt-4 px-4 py-2 rounded-full bg-white text-sm">Tutup</button>
    </div>
  );
};

export const ScanInput = ({ value, onChange, testid, placeholder = "Kode spool, mis. SPL00001" }) => {
  const [cam, setCam] = useState(false);
  return (
    <div className="flex gap-2">
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} placeholder={placeholder}
        className="flex-1 min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono" />
      <button type="button" data-testid={`${testid}-camera`} onClick={() => setCam(true)} className="px-3 rounded-lg bg-slate-900 text-white inline-flex items-center gap-1 text-sm"><ScanLine className="w-4 h-4" /> Scan</button>
      {cam && <CameraScanner onCode={(c) => { onChange(c.trim().toUpperCase()); setCam(false); }} onClose={() => setCam(false)} />}
    </div>
  );
};

export const STATUS_STYLE = {
  PLANNED: "bg-slate-100 text-slate-700", PREP: "bg-amber-100 text-amber-700", READY: "bg-sky-100 text-sky-700", RUNNING: "bg-emerald-100 text-emerald-700",
  ERROR: "bg-rose-100 text-rose-700", DONE: "bg-violet-100 text-violet-700",
};
export const STATUS_LABEL = { PLANNED: "Menunggu material", PREP: "Material sebagian", READY: "Siap jalan", RUNNING: "Mesin berjalan", ERROR: "Error", DONE: "Selesai" };
